import { useState } from 'react';
import { FadeIn } from '../components/ui/AnimatedSection';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'forgot_password'>('login');
  
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMsg(null);

    if (!supabase) {
      setError(t('login.errorConfig'));
      setLoading(false);
      return;
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (signInError) {
      setError(t('login.errorAuth'));
    } else {
      setMsg(t('login.msgSuccess'));
      setTimeout(() => navigate('/dashboard'), 1500);
    }
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t('login.errorEmailReq'));
      return;
    }
    setLoading(true);
    setError(null);
    setMsg(null);

    if (!supabase) {
      setError(t('login.errorConfig'));
      setLoading(false);
      return;
    }

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password',
    });

    if (resetError) {
      setError(t('login.errorReset'));
    } else {
      setMsg(t('login.msgResetSuccess'));
      setMode('login');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      
      {/* Left Side: Branding */}
      <div className="hidden md:flex md:w-1/2 relative bg-cover bg-center items-center justify-center overflow-hidden"
           style={{ backgroundImage: 'url("/assets/luxury_interior.webp")' }}>
        <div className="absolute inset-0 bg-black-deep/70"></div>
        <FadeIn className="relative z-10 text-center px-12">
          <img src="/assets/logo_v2.webp" alt="Logo" className="h-32 w-auto mx-auto mb-8 brightness-0 invert opacity-90" width="400" height="150" />
          <h2 className="text-3xl font-serif text-white tracking-widest mb-4">{t('login.brandTitle')}</h2>
          <div className="font-script text-2xl text-gold-500 mb-8">{t('login.brandSubtitle')}</div>
          <p className="text-gray-300 font-light text-sm max-w-sm mx-auto leading-relaxed">
            {t('login.brandDesc')}
          </p>
        </FadeIn>
      </div>

      {/* Right Side: Login Form (White Background) */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <FadeIn className="w-full max-w-md">
          <div className="md:hidden text-center mb-12">
            <img src="/assets/logo_v2.webp" alt="Logo" className="h-20 w-auto mx-auto mb-4" width="400" height="150" />
            <h2 className="text-2xl font-serif text-black-deep tracking-widest">{t('login.brandTitle')}</h2>
          </div>

          <h1 className="text-3xl font-serif text-black-deep mb-2">{t('login.formTitle')}</h1>
          <p className="text-gray-500 font-light mb-10 text-sm">
            {mode === 'login' ? t('login.formDescLogin') : t('login.formDescReset')}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-2 border-red-500 text-red-700 text-sm">
              {error}
            </div>
          )}

          {msg && (
            <div className="mb-6 p-4 bg-green-50 border-l-2 border-green-500 text-green-700 text-sm">
              {msg}
            </div>
          )}

          <form className="space-y-6" onSubmit={mode === 'login' ? handleLogin : handleResetPassword}>
            <div>
              <label htmlFor="login-email" className="block text-xs uppercase tracking-wider text-gray-500 mb-2 font-medium">{t('login.labelEmail')}</label>
              <input 
                id="login-email"
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-gray-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:bg-white transition-colors rounded-sm"
                placeholder="votre@email.com"
              />
            </div>
            
            {mode === 'login' && (
              <div>
                <label htmlFor="login-password" className="block text-xs uppercase tracking-wider text-gray-500 mb-2 font-medium">{t('login.labelPassword')}</label>
                <input 
                  id="login-password"
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-gray-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:bg-white transition-colors rounded-sm"
                  placeholder="••••••••"
                />
              </div>
            )}

            {mode === 'login' && (
              <div className="flex items-center justify-between text-xs">
                <label htmlFor="login-remember" className="flex items-center space-x-2 cursor-pointer">
                  <input id="login-remember" type="checkbox" className="accent-gold-500" />
                  <span className="text-gray-500">{t('login.rememberMe')}</span>
                </label>
                <button 
                  type="button" 
                  onClick={() => setMode('forgot_password')}
                  className="text-gold-600 hover:text-gold-500 transition-colors font-medium"
                >
                  {t('login.forgotPass')}
                </button>
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-black-deep text-white uppercase tracking-widest text-sm py-4 rounded-full shadow-lg hover:shadow-xl hover:bg-gray-900 hover:-translate-y-1 transition-all duration-300 mt-4 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? t('login.btnLoading') : (mode === 'login' ? t('login.btnLogin') : t('login.btnReset'))}
            </button>

            {mode === 'forgot_password' && (
              <div className="text-center mt-4">
                <button 
                  type="button" 
                  onClick={() => setMode('login')}
                  className="text-xs text-gray-500 hover:text-black-deep transition-colors"
                >
                  {t('login.backLogin')}
                </button>
              </div>
            )}
          </form>

          <div className="mt-12 text-center border-t border-gray-100 pt-8">
            <p className="text-xs text-gray-400 mb-4 font-light">
              {t('login.footerNote')}
            </p>
            <Link to="/" className="text-xs text-gold-600 font-medium hover:text-gold-500 transition-colors">
              &larr; {t('login.backHome')}
            </Link>
          </div>
        </FadeIn>
      </div>

    </div>
  );
}
